import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Key, Plus, Copy, Trash2, Edit3, Check, X, AlertTriangle,
  Download, Shield, Activity, Clock, Wifi, WifiOff,
  ToggleLeft, ToggleRight, RefreshCw, ExternalLink, Terminal
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiDelete, apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog } from '../../components/AdminUi';

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [copied, setCopied] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { fetchKeys(); }, []);
  useEffect(() => { if (showCreate && inputRef.current) inputRef.current.focus(); }, [showCreate]);

  const fetchKeys = async () => {
    try {
      setKeys(await apiGet(API_ROUTES.app.apiKeys));
    } catch (e) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const res = await apiFetch(API_ROUTES.app.apiKeys, {
        method: 'POST',
        body: { name: newKeyName },
      });
      const data = await res.json();
      if (res.ok) {
        setCreatedKey(data);
        setNewKeyName('');
        setShowCreate(false);
        fetchKeys();
        toast.success('API key created');
      } else {
        toast.error(data.detail || 'Failed to create key');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setCreating(false);
    }
  };

  const toggleKey = async (id, currentState) => {
    try {
      const res = await apiFetch(`${API_ROUTES.app.apiKeys}/${id}`, {
        method: 'PATCH',
        body: { is_active: !currentState },
      });
      if (res.ok) {
        fetchKeys();
        toast.success(currentState ? 'Key deactivated' : 'Key activated');
      }
    } catch (e) {
      toast.error('Failed to update key');
    }
  };

  const renameKey = async (id) => {
    if (!editName.trim()) { setEditingId(null); return; }
    try {
      const res = await apiFetch(`${API_ROUTES.app.apiKeys}/${id}`, {
        method: 'PATCH',
        body: { name: editName },
      });
      if (res.ok) {
        fetchKeys();
        toast.success('Key renamed');
      }
    } catch (e) {
      toast.error('Failed to rename');
    }
    setEditingId(null);
  };

  const deleteKey = async () => {
    if (!deleteTarget) return;
    try {
      await apiDelete(`${API_ROUTES.app.apiKeys}/${deleteTarget.id}`);
      toast.success('API key deleted');
      setDeleteTarget(null);
      fetchKeys();
    } catch (e) {
      toast.error('Failed to delete key');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  const formatDate = (iso) => {
    if (!iso) return '�';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const timeAgo = (iso) => {
    if (!iso) return 'Never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(iso);
  };

  const totalRequests = keys.reduce((sum, k) => sum + (k.total_requests || 0), 0);
  const activeKeys = keys.filter(k => k.is_active).length;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20 flex items-center justify-center">
              <Key size={18} className="text-emerald-400" />
            </div>
            API Keys
          </h1>
          <p className="text-gray-500 text-sm mt-2 ml-12">Manage your MT4/MT5 connection keys. Each key links your terminal to your dashboard.</p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setCreatedKey(null); }}
          className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium px-5 py-2.5 rounded-lg hover:opacity-90 transition text-sm"
        >
          <Plus size={16} />
          New Key
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Key size={16} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{activeKeys}<span className="text-gray-600 text-sm font-normal">/{keys.length}</span></p>
              <p className="text-xs text-gray-500">Active Keys</p>
            </div>
          </div>
        </div>
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Activity size={16} className="text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{totalRequests.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Uploads</p>
            </div>
          </div>
        </div>
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Shield size={16} className="text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{keys.length > 0 ? timeAgo(keys.sort((a,b) => new Date(b.last_used||0) - new Date(a.last_used||0))[0]?.last_used) : '�'}</p>
              <p className="text-xs text-gray-500">Last Sync</p>
            </div>
          </div>
        </div>
      </div>

      {/* Created Key Alert */}
      {createdKey && (
        <div className="bg-gradient-to-r from-amber-500/5 to-orange-500/5 border border-amber-500/30 rounded-xl p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={20} className="text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-amber-200 font-semibold mb-1">Your new API key</h3>
              <p className="text-amber-400/60 text-xs mb-4">Copy and save this key now. For security, it won't be shown again in full.</p>
              <div className="flex items-center gap-2 bg-[#0d1117] rounded-lg px-4 py-3 border border-amber-500/20">
                <Terminal size={14} className="text-amber-400/50 flex-shrink-0" />
                <code className="text-emerald-400 text-sm flex-1 font-mono select-all">{createdKey.key}</code>
                <button
                  onClick={() => copyToClipboard(createdKey.key, 'new')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 rounded-md text-amber-300 text-xs font-medium transition"
                >
                  {copied === 'new' ? <Check size={12} /> : <Copy size={12} />}
                  {copied === 'new' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <span className="text-xs text-gray-500">Name: <span className="text-gray-300">{createdKey.name}</span></span>
                <button onClick={() => setCreatedKey(null)} className="text-xs text-gray-500 hover:text-gray-300 transition">Dismiss</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Key Form */}
      {showCreate && !createdKey && (
        <div className="bg-dark-card border border-emerald-500/20 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
            <Plus size={14} className="text-emerald-400" />
            Create New API Key
          </h3>
          <p className="text-xs text-gray-500 mb-4">Give your key a descriptive name so you can identify which terminal it's connected to.</p>
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g., Coinexx Live #977419, Vantage Demo, My VPS"
              className="flex-1 bg-[#0d1117] border border-dark-border rounded-lg px-4 py-2.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 text-sm transition"
              onKeyDown={(e) => e.key === 'Enter' && createKey()}
            />
            <button
              onClick={createKey}
              disabled={creating || !newKeyName.trim()}
              className="px-5 py-2.5 bg-emerald-500 text-white rounded-lg font-medium text-sm hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {creating ? <RefreshCw size={14} className="animate-spin" /> : 'Generate Key'}
            </button>
            <button
              onClick={() => { setShowCreate(false); setNewKeyName(''); }}
              className="px-4 py-2.5 text-gray-400 hover:text-gray-200 text-sm transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Keys List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={24} className="text-emerald-400 animate-spin" />
        </div>
      ) : keys.length === 0 && !createdKey ? (
        <div className="bg-dark-card border border-dark-border rounded-xl p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <Key size={28} className="text-emerald-500/40" />
          </div>
          <h3 className="text-gray-200 font-semibold mb-2">No API keys yet</h3>
          <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
            Create your first API key to connect your MT4/MT5 terminal and start syncing trade data.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium px-6 py-3 rounded-lg hover:opacity-90 transition"
          >
            <Plus size={16} />
            Create Your First Key
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map((k) => (
            <div key={k.id} className={`bg-dark-card border rounded-xl p-5 transition-all ${
              k.is_active ? 'border-dark-border hover:border-emerald-500/20' : 'border-dark-border/50 opacity-60'
            }`}>
              <div className="flex items-start gap-4">
                {/* Status indicator */}
                <div className="relative mt-1">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    k.is_active ? 'bg-emerald-500/10' : 'bg-gray-800'
                  }`}>
                    {k.is_active ? <Wifi size={18} className="text-emerald-400" /> : <WifiOff size={18} className="text-gray-600" />}
                  </div>
                  {k.is_active && k.last_used && (Date.now() - new Date(k.last_used).getTime()) < 120000 && (
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-dark-card animate-pulse" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Name row */}
                  <div className="flex items-center gap-2 mb-1">
                    {editingId === k.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="bg-[#0d1117] border border-emerald-500/30 rounded px-2 py-1 text-sm text-gray-200 focus:outline-none w-48"
                          onKeyDown={(e) => { if (e.key === 'Enter') renameKey(k.id); if (e.key === 'Escape') setEditingId(null); }}
                          autoFocus
                        />
                        <button onClick={() => renameKey(k.id)} className="text-emerald-400 hover:text-emerald-300"><Check size={14} /></button>
                        <button onClick={() => setEditingId(null)} className="text-gray-500 hover:text-gray-300"><X size={14} /></button>
                      </div>
                    ) : (
                      <>
                        <span className="text-gray-100 font-medium text-sm">{k.name || 'Unnamed Key'}</span>
                        <button
                          onClick={() => { setEditingId(k.id); setEditName(k.name || ''); }}
                          className="text-gray-600 hover:text-gray-400 transition"
                        >
                          <Edit3 size={12} />
                        </button>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          k.is_active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-gray-800 text-gray-500 border border-gray-700'
                        }`}>
                          {k.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Key value */}
                  <div className="flex items-center gap-2 mb-3">
                    <code className="text-xs text-gray-500 font-mono bg-[#0d1117] px-2 py-1 rounded">{k.key}</code>
                    <button
                      onClick={() => copyToClipboard(k.key, k.id)}
                      className="text-gray-600 hover:text-emerald-400 transition"
                      title="Copy key"
                    >
                      {copied === k.id ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                    </button>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-5 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5">
                      <Clock size={11} />
                      Created {formatDate(k.created_at)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Activity size={11} />
                      {(k.total_requests || 0).toLocaleString()} uploads
                    </span>
                    <span className="flex items-center gap-1.5">
                      <RefreshCw size={11} />
                      Last sync: {timeAgo(k.last_used)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleKey(k.id, k.is_active)}
                    className={`p-2 rounded-lg transition ${k.is_active ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-gray-600 hover:bg-gray-800'}`}
                    title={k.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {k.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                  </button>
                  <button
                    onClick={() => setDeleteTarget(k)}
                    className="p-2 text-gray-600 hover:text-red-400 rounded-lg hover:bg-red-500/5 transition"
                    title="Delete key"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Setup Guide */}
      <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-dark-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
            <Terminal size={14} className="text-emerald-400" />
            MT4/MT5 Setup Guide
          </h3>
          <Link to="/settings/ea-download" className="text-xs text-emerald-400 hover:underline flex items-center gap-1">
            Download EA <ExternalLink size={10} />
          </Link>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">One-Time Setup</h4>
              <ol className="space-y-3">
                {[
                  'Download TrackMyFxEA EA (.mq4 or .mq5)',
                  'Copy to MT4/MT5 ? Experts folder',
                  'Tools ? Options ? Expert Advisors ? Allow WebRequest',
                  'Add URL: https://api.trackmyfxea.com',
                ].map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm text-gray-400">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">EA Parameters</h4>
              <div className="bg-[#0d1117] rounded-lg p-4 font-mono text-xs space-y-1.5">
                <div><span className="text-gray-500">EnableCloudUpload</span> = <span className="text-emerald-400">true</span></div>
                <div><span className="text-gray-500">CloudApiKey</span> = <span className="text-amber-400">"tme_live_your_key_here"</span></div>
                <div><span className="text-gray-500">CloudServerUrl</span> = <span className="text-blue-400">"https://api.trackmyfxea.com"</span></div>
                <div><span className="text-gray-500">CloudUploadInterval</span> = <span className="text-purple-400">60</span></div>
                <div><span className="text-gray-500">EnableLocalFileExport</span> = <span className="text-emerald-400">true</span></div>
              </div>
              <p className="text-[11px] text-gray-600 mt-2">Data syncs every 60 seconds. Local file export still works alongside cloud.</p>
            </div>
          </div>
        </div>
      </div>

      <AdminConfirmDialog
        open={!!deleteTarget}
        title="Delete API Key"
        message={`Delete "${deleteTarget?.name || 'Unnamed Key'}" permanently? EAs using this key will stop syncing immediately. Any linked account data that can be matched to this key will also be removed.`}
        confirmLabel="Delete Key"
        requireText="DELETE"
        onConfirm={deleteKey}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

